import React, { Suspense } from 'react'
import {
  createRootRouteWithContext,
  ErrorComponent,
  Outlet,
} from '@tanstack/react-router'
import { QueryClient } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { ToastContainer } from 'react-toastify'
import { useTranslation } from 'react-i18next'

const TanStackRouterDevtools =
  process.env.NODE_ENV === 'production'
    ? () => null // Render nothing in production
    : React.lazy(() =>
        // Lazy load in development
        import('@tanstack/react-router-devtools').then((res) => ({
          default: res.TanStackRouterDevtools,
          // For Embedded Mode
          // default: res.TanStackRouterDevtoolsPanel
        })),
      )

import 'react-toastify/dist/ReactToastify.css'

import '@/theme/global.css'
import '@/theme/layout.css'

import { AuthContext, isPrivilegedUser, useAuth } from '@/auth'
import { Header } from '@/components/layout'
import { Button } from '@/components/Button'

// Areas of the app that any authenticated user may access. Everything else
// requires a privileged role (admin, nutritionist or ambassador) — the
// backend responds 403 to the APIs those pages use.
const PUBLIC_PATHS = ['/', '/usage-statistics']

function isPublicPath(pathname: string) {
  // location.pathname includes the router basepath
  const path = pathname.replace(/^\/nutritionist/, '') || '/'
  return PUBLIC_PATHS.some((p) => path === p || path.startsWith(`${p}/`))
}

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient
  auth: AuthContext
}>()({
  component: RootComponent,
  beforeLoad: ({ location, context: { auth } }) => {
    if (isPublicPath(location.pathname)) {
      return
    }

    if (!isPrivilegedUser(auth)) {
      throw { isAccessDenied: true }
    }
  },
  errorComponent: ({ error }) => {
    if ((error as { isAccessDenied?: boolean })?.isAccessDenied) {
      // Rendered inside the usual shell so the navigation stays visible
      return (
        <div className="app">
          <Header />
          <div className="container">
            <AccessDenied />
          </div>
        </div>
      )
    }

    return <ErrorComponent error={error} />
  },
})

function AccessDenied() {
  const { t } = useTranslation()
  const auth = useAuth()

  return (
    <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '80px' }}>
      <div style={{ textAlign: 'center', maxWidth: '480px' }}>
        <h1>{t('accessDenied.title')}</h1>
        <p>{t('accessDenied.message')}</p>
        <Button onClick={auth.logout}>{t('menu.logout')}</Button>
      </div>
    </div>
  )
}

function RootComponent() {
  return (
    <div className="app">
      <Header />
      <div className="container">
        <Outlet />
      </div>
      <ToastContainer position="top-center" hideProgressBar draggablePercent={0} />

      <Suspense>
        <TanStackRouterDevtools />
      </Suspense>
      <ReactQueryDevtools initialIsOpen={false} />
    </div>
  )
}
